import { ActivityEntityName } from '../../../../entities/shared/activity.entity.name';
import { MetadataEntityData, MetadataSecondaryFilterAlternate } from '../model/metadata.entity.data';
import { MetadataEntityRecordScope } from '../model/metadata.entity.record.scope';

/**
 * One link in an entity-owned secondaryFilter chain.
 * Job.secondaryFilter → Stage, Stage.secondaryFilter → EmbeddedCode, …
 */
export interface MetadataEmbeddedLevel {
  /** Absolute path from the root document, e.g. `workOrders` or `workOrders.codes`. */
  absolutePath: string;
  /** Segment relative to the parent entity, e.g. `codes`. */
  arrayPath: string;
  /** Metadata describing records in this child array. */
  metadata: MetadataEntityData;
  /** Whether this link supplies the first-level default when no explicit scope is provided. */
  unwind: boolean;
  /** 0-based depth in the chain. */
  index: number;
  /**
   * Sibling arrays unioned into `arrayPath` before filter/unwind (same parent entity).
   * Empty when the level has only the primary array.
   */
  alternateArrayPaths: MetadataSecondaryFilterAlternate[];
}

/**
 * Resolves ordered `secondaryFilter` links from root metadata. Child metadata is supplied explicitly
 * or inferred from the matching array field. Resolution stops when a link is incomplete or would
 * revisit metadata already present in the chain.
 */
export function resolveEmbeddedLevels(root: MetadataEntityData | null | undefined): MetadataEmbeddedLevel[] {

  const levels: MetadataEmbeddedLevel[] = [];
  if (!root) return levels;

  const seen = new Set<MetadataEntityData>([root]);
  let current: MetadataEntityData | undefined = root;
  let absolutePrefix = '';

  while (current?.secondaryFilter?.arrayPath) {
    const arrayPath = current.secondaryFilter.arrayPath;
    const absolutePath = absolutePrefix ? `${absolutePrefix}.${arrayPath}` : arrayPath;

    const metadata = current.secondaryFilter.metadata
      ?? resolveArrayFieldMetadata(current, arrayPath);

    if (!metadata?.fields?.length) break;
    if (seen.has(metadata)) break;
    seen.add(metadata);

    levels.push({
      absolutePath,
      arrayPath,
      metadata,
      unwind: current.secondaryFilter.unwind ?? false,
      index: levels.length,
      alternateArrayPaths: current.secondaryFilter.alternateArrayPaths ?? [],
    });

    absolutePrefix = absolutePath;
    current = metadata;
  }

  return levels;
}

/**
 * An ancestor of the row grain, as a swimlane candidate.
 *
 * A parent is a concept ("Job"), not a field ("Job Number") — the axis is the parent record, and the
 * identifier is only how a row points at it. `displayName` is therefore the entity's singular name
 * and `identifierPath` the absolute path callers group and page by.
 */
export interface MetadataParentLevel {
  /** -1 for the root document, else the chain level's index. */
  index: number;
  metadata: MetadataEntityData;
  entity: ActivityEntityName;
  /** Singular entity name, e.g. `Job` / `Stage`. */
  displayName: string;
  /** '' for the root document, else the level's absolute path. */
  absolutePath: string;
  /** Absolute path of this level's `identifier === true` field. */
  identifierPath: string;
}

/**
 * Ancestors of a row grain, root first — the parents a board can swimlane or page by.
 *
 * Empty on a non-embedded scope: with nothing unwound beneath it there is no parent to group into.
 * A level without an identifier field is skipped rather than guessed at, since the caller needs a
 * real path to group by.
 */
export function resolveParentLevels(root: MetadataEntityData | null | undefined, recordScope: MetadataEntityRecordScope | string | null | undefined): MetadataParentLevel[] {

  if (!root || !isEmbeddedRecordScope(recordScope)) return [];

  const levels = resolveEmbeddedLevels(root);
  if (!levels.length) return [];

  const rowLevel = resolveRowLevelIndex(recordScope, levels);
  const ancestors = [
    { index: -1, metadata: root, absolutePath: '' },
    ...levels.filter(level => level.index < rowLevel).map(level => ({ index: level.index, metadata: level.metadata, absolutePath: level.absolutePath })),
  ];

  return ancestors.flatMap(ancestor => {
    const identifierPath = resolveIdentifierPath(ancestor.metadata, ancestor.absolutePath);
    if (!identifierPath) return [];
    return [{
      index: ancestor.index,
      metadata: ancestor.metadata,
      entity: ancestor.metadata.type,
      displayName: ancestor.metadata.displayName ?? ancestor.metadata.name,
      absolutePath: ancestor.absolutePath,
      identifierPath,
    }];
  });
}

/**
 * Whether a group key names a parent RECORD rather than one of its fields.
 *
 * Only the identifier gets parent paging for free: grouping by it means one document per group, so
 * `$skip`/`$limit` over documents already keeps every group whole.
 */
function isParentIdentifier(fieldId: string, levels: MetadataEmbeddedLevel[], rootMetadata?: MetadataEntityData | null): boolean {

  if (fieldId === '_id') return true;

  const rootIdentifier = rootMetadata ? resolveIdentifierPath(rootMetadata, '') : null;
  if (rootIdentifier && fieldId === rootIdentifier) return true;

  return levels.some(level => {
    const identifierPath = resolveIdentifierPath(level.metadata, level.absolutePath);
    return !!identifierPath && fieldId === identifierPath;
  });
}

function resolveIdentifierPath(metadata: MetadataEntityData, prefix: string): string | null {

  const identifier = metadata.fields?.find(f => f.identifier === true);
  const path = identifier?.path ?? identifier?.name;
  if (!path) return null;
  return prefix ? `${prefix}.${path}` : path;
}

/**
 * Resolve which absolute paths to $unwind for a view grain.
 *
 * - `Primary` / absent-with-no-default → none
 * - `Secondary` → first chain level only (existing stage boards)
 * - absolute path e.g. `workOrders.codes` → every level up to and including that path
 * - default (no scope): first level only when it declares `unwind: true`
 * - an unrelated path → none; a path extending a valid level still unwinds matching ancestors
 */
export function resolveUnwindPaths(
  recordScope: MetadataEntityRecordScope | string | null | undefined,
  levels: MetadataEmbeddedLevel[],
): string[] {

  if (!levels.length) return [];
  if (recordScope === MetadataEntityRecordScope.Primary) return [];

  if (recordScope === MetadataEntityRecordScope.Secondary) {
    return [levels[0].absolutePath];
  }

  if (isPathScope(recordScope)) {
    return levels
      .filter(level => isLevelAtOrAboveScope(level.absolutePath, recordScope))
      .map(level => level.absolutePath);
  }

  // No explicit grain: preserve legacy default — first level only if unwind: true
  if (levels[0]?.unwind) return [levels[0].absolutePath];
  return [];
}

/** True for Secondary or any non-enum path string; this classifier does not validate against metadata. */
export function isEmbeddedRecordScope(recordScope: MetadataEntityRecordScope | string | null | undefined): boolean {

  if (!recordScope || recordScope === MetadataEntityRecordScope.Primary || recordScope === MetadataEntityRecordScope.Any) {
    return false;
  }
  return recordScope === MetadataEntityRecordScope.Secondary || isPathScope(recordScope);
}

/** Scope stamped on merged fields for a chain level: first level stays `Secondary` for back-compat. */
export function scopeForEmbeddedLevel(level: MetadataEmbeddedLevel): MetadataEntityRecordScope | string {

  return level.index === 0
    ? MetadataEntityRecordScope.Secondary
    : level.absolutePath;
}

/** Context used to derive paging and count grain for embedded rows. */
export interface ResolveEmbeddedPagingOptions {
  /** Bounded single-shot fetch; retained to describe the caller's request mode. */
  unpaged?: boolean;
  /** Root entity metadata (e.g. Job) — used so groupBy jobNumber/_id resolves as parent, not stage. */
  rootMetadata?: MetadataEntityData | null;
}

export interface EmbeddedPagingResolution {
  /**
   * When true, $skip/$limit run after $unwind (exact pageSize of stage/item rows).
   * When false, $skip/$limit run on parent docs then unwind — group-complete for job-level pages.
   */
  pageOnEmbedded: boolean;
  /**
    * When true, count pipeline $unwinds to row grain (stage/item total).
    * When false, count stays on root documents.
    */
  countOnEmbedded: boolean;
  /**
   * The PARENT-level group key whose groups must be made contiguous before parent paging.
   *
   * Parent paging keeps a group whole for free only when the key IS the parent — every row of a job
   * rides one document, so 50 jobs is 50 whole groups. Any OTHER parent field (a job's billing
   * company, its owner) spreads its group over many documents, and paging jobs in sort order then
   * cut those groups across every page: live, three billing companies each appeared on all four
   * pages with a different count. The parent sort has to lead with this key so the groups arrive
   * contiguous; see `parentGroupBy` in `mongo.query.builder.ts`.
   *
   * Null when the key is the parent's own identifier, or when there is no parent grouping at all.
   */
  parentGroupBy?: string | null;
  /**
   * The group key to page WHOLE GROUPS by, e.g. `workOrders.stageOwner` or, on a Primary board, `owner`.
   *
   * Set when a single group key names the grain the page is cut at — the unwound row on an embedded
   * board, the root document on a Primary one. Neither has a unit of $skip/$limit that already holds
   * a group together, so the pipeline builds one; see `mongo.row.group.paging.ts`.
   *
   * NOT set for a parent key on an embedded board: a parent document is already one whole unit, so
   * paging parents (pageOnEmbedded false) keeps its rows together for free. Also not set for two
   * keys, which have no single unit to keep whole.
   *
   * Null leaves `pageOnEmbedded`/`countOnEmbedded` as the whole story (flat paging).
   */
  rowGroupBy?: string | null;
}

/**
 * Derive paging/count grain from recordScope + view groupBy / subGroupBy.
 *
 * - Parent group key(s) → page/count root documents, then unwind to rows.
 * - Only row-level group keys, or a flat embedded list → page/count rows after unwind.
 * - A single group key AT the page's own grain → whole-group paging (`rowGroupBy`), which is the
 *   Primary board's only way to keep a group together and the embedded board's for a row-level key.
 */
export function resolveEmbeddedPaging(
  recordScope: MetadataEntityRecordScope | string | null | undefined,
  groupBy: string | null | undefined,
  levels: MetadataEmbeddedLevel[],
  options?: ResolveEmbeddedPagingOptions & { subGroupBy?: string | null },
): EmbeddedPagingResolution {

  const groupKeys = [groupBy, options?.subGroupBy]
    .map(normalizeGroupBy)
    .filter((g): g is string => g != null);

  // Primary grain (or an entity with no chain at all): the page is cut at the root document, so a
  // single root-level group key is exactly the case whole-group paging was built for. Without this a
  // groupBy is ACCEPTED AND IGNORED here — pages come back as plain cuts and the client groups each
  // page on its own, so one group appears on several pages with a different count each time.
  if (!isEmbeddedRecordScope(recordScope) || !levels.length) {
    const rootGroupBy = groupKeys.length === 1 && resolveFieldLevelIndex(groupKeys[0], levels, options?.rootMetadata) < 0
      ? groupKeys[0]
      : null;
    return { pageOnEmbedded: false, countOnEmbedded: false, rowGroupBy: rootGroupBy };
  }

  const rowLevel = resolveRowLevelIndex(recordScope, levels);
  const groupLevels = groupKeys.map(g => resolveFieldLevelIndex(g, levels, options?.rootMetadata));

  const hasParentGrouping = groupLevels.some(level => level < rowLevel);

  if (hasParentGrouping) {
    // Paging parents keeps a group whole only when the key IS the parent. Any other parent field
    // spreads its group over many documents, so the parent sort must lead with it — otherwise the
    // group is cut wherever the page boundary happens to fall.
    const parentGroupBy = groupKeys.length === 1 && groupLevels[0] < rowLevel && !isParentIdentifier(groupKeys[0], levels, options?.rootMetadata)
      ? groupKeys[0]
      : null;
    return { pageOnEmbedded: false, countOnEmbedded: false, parentGroupBy };
  }

  // All group keys at row level (or deeper) → page rows
  if (groupLevels.length > 0 && groupLevels.every(level => level >= rowLevel)) {
    // A lone key exactly at row grain is a swimlane axis: pages hold whole groups (see rowGroupBy).
    // Two keys, or a key deeper than the row, have no single unit to keep whole — page flat rows.
    const rowGroupBy = groupKeys.length === 1 && groupLevels[0] === rowLevel ? groupKeys[0] : null;
    return { pageOnEmbedded: true, countOnEmbedded: true, rowGroupBy };
  }

  // A flat embedded list is presented and counted as rows, so paging must use the same grain.
  return { pageOnEmbedded: true, countOnEmbedded: true };
}

/**
 * Resolves row-grain depth. Secondary maps to zero and valid paths map to their deepest matching
 * level. Missing or invalid path scopes fall back to the first level for compatibility.
 */
export function resolveRowLevelIndex(
  recordScope: MetadataEntityRecordScope | string | null | undefined,
  levels: MetadataEmbeddedLevel[],
): number {

  if (!levels.length) return -1;
  if (recordScope === MetadataEntityRecordScope.Secondary) return 0;
  if (isPathScope(recordScope)) {
    const exact = levels.findIndex(l => l.absolutePath === recordScope);
    if (exact >= 0) return exact;
    // Longest prefix match if scope is a field path under a level
    let best = 0;
    for (const level of levels) {
      if (recordScope === level.absolutePath || recordScope.startsWith(`${level.absolutePath}.`)) {
        best = level.index;
      }
    }
    return best;
  }
  return 0;
}

/**
 * Which embedded level a field path belongs to (-1 = root/parent document).
 * Uses longest absolutePath prefix; bare names on the root entity prefer parent (-1) so
 * groupBy jobNumber / _id keeps job groups intact even when Stage also has jobNumber.
 */
export function resolveFieldLevelIndex(
  fieldId: string,
  levels: MetadataEmbeddedLevel[],
  rootMetadata?: MetadataEntityData | null,
): number {

  if (!fieldId) return -1;

  let best = -1;
  for (const level of levels) {
    if (fieldId === level.absolutePath || fieldId.startsWith(`${level.absolutePath}.`)) {
      if (level.index > best) best = level.index;
    }
  }
  if (best >= 0) return best;

  const segment = fieldId.includes('.') ? fieldId.substring(fieldId.lastIndexOf('.') + 1) : fieldId;
  const rootKeys = rootMetadata ? filterableKeySet(rootMetadata) : new Set<string>();
  // Prefer root when the field exists on the parent entity (group-by-job)
  if (rootKeys.has(fieldId) || rootKeys.has(segment) || fieldId === '_id' || segment === '_id') {
    return -1;
  }

  // Exclusive to one embedded level
  const claimed = new Set<string>(rootKeys);
  for (let i = 0; i < levels.length; i++) {
    const keys = filterableKeySet(levels[i].metadata);
    const exclusive = [...keys].filter(k => !claimed.has(k));
    for (const k of keys) claimed.add(k);
    if (exclusive.includes(segment) || exclusive.includes(fieldId)) return i;
  }
  for (let i = levels.length - 1; i >= 0; i--) {
    const keys = filterableKeySet(levels[i].metadata);
    if (keys.has(segment) || keys.has(fieldId)) return i;
  }

  // Unknown bare path → treat as root (safer for group-complete job grouping)
  return -1;
}

function normalizeGroupBy(groupBy: string | null | undefined): string | null {

  if (groupBy == null || groupBy === '' || groupBy === 'none') return null;
  return groupBy;
}

function filterableKeySet(metadata: MetadataEntityData): Set<string> {

  const keys = new Set<string>();
  for (const field of metadata?.fields ?? []) {
    if (!field) continue;
    if (field.name) keys.add(field.name);
    if (field.attributeFilterName) keys.add(field.attributeFilterName);
    if (field.path) keys.add(field.path);
  }
  return keys;
}

function resolveArrayFieldMetadata(parent: MetadataEntityData, arrayPath: string): MetadataEntityData | undefined {

  const field = parent.fields?.find(f => f.name === arrayPath || f.path === arrayPath);
  const typeData = field?.typeData as MetadataEntityData | undefined;
  return typeData?.fields?.length ? typeData : undefined;
}

function isPathScope(recordScope: MetadataEntityRecordScope | string | null | undefined): recordScope is string {

  if (!recordScope || typeof recordScope !== 'string') return false;
  if (recordScope === MetadataEntityRecordScope.Primary) return false;
  if (recordScope === MetadataEntityRecordScope.Secondary) return false;
  if (recordScope === MetadataEntityRecordScope.Any) return false;
  return true;
}

/** Level is included in the unwind set when it is the grain or an ancestor of the grain. */
function isLevelAtOrAboveScope(absolutePath: string, grain: string): boolean {

  return grain === absolutePath || grain.startsWith(`${absolutePath}.`);
}
